import { useTranslation } from "react-i18next"
import { useLazyLoadQuery } from "react-relay"
import { FetchPolicy, graphql } from "relay-runtime"
import {
  useCareProviderWithOriginQuery,
  useCareProviderWithOriginQuery$data,
} from "@/modules/care-provider/services/__generated__/useCareProviderWithOriginQuery.graphql"

export type CareProviderWithOrigin =
  | (useCareProviderWithOriginQuery$data["ch_careProviderByRelationalId"] & {
      originPhoneNumber: string | null | undefined
      originEmail: string | null | undefined
      originWebsite: string | null | undefined
    })
  | undefined

const careProviderWithOriginQuery = graphql`
  query useCareProviderWithOriginQuery($id: ID!, $language: String!, $skip: Boolean = false) {
    ch_careProviderByRelationalId(id: $id) @skip(if: $skip) {
      id
      businessName
      title
      firstName
      lastName
      groupName(language: $language)
      careProviderSpecialtyTranslations(language: $language) {
        name
      }
      officialLanguageLookupTranslations(language: $language) {
        isoLanguageName
      }
      careProviderGroup {
        groupType
      }
      country {
        countryName(language: $language)
      }
      relationType
      network {
        name
      }
      creditorCodeRegister
      isMedgatePartnerNetworkMember
      insuranceCareProviderListIds
      isTakesNewPatients
      phoneNumber
      email
      website
      globalLocationNumber
      countryCode
      city
      postalCode
      streetAddress
      additionalAddress
      isActive
      lastChange {
        changedAt
        changedBy
      }
      careProviderSearchHistories {
        createdAt
        ...historyEventsTableFragment
      }
      relationBusinessName
      relationFirstName
      relationLastName
      relationTitle
      billsVia {
        businessName
        firstName
        lastName
        title
      }
      detailedSchedule {
        day
        workingType
        periods {
          start
          end
        }
      }
    }
    ch_careProviderOrigin(careProviderRelationId: $id) @skip(if: $skip) {
      phoneNumber
      email
      website
    }
  }
`

const useCareProviderWithOrigin = (id?: string | null, skip?: boolean, fetchPolicy?: FetchPolicy) => {
  const { i18n } = useTranslation()
  const { ch_careProviderByRelationalId, ch_careProviderOrigin } = useLazyLoadQuery<useCareProviderWithOriginQuery>(
    careProviderWithOriginQuery,
    {
      id: id ?? "",
      language: i18n.language.toUpperCase(),
      skip: skip || !id,
    },
    {
      fetchPolicy,
    },
  )

  if (!ch_careProviderByRelationalId) {
    return undefined
  }

  return {
    ...ch_careProviderByRelationalId,
    originPhoneNumber: ch_careProviderOrigin?.phoneNumber,
    originEmail: ch_careProviderOrigin?.email,
    originWebsite: ch_careProviderOrigin?.website,
  }
}

export { useCareProviderWithOrigin, careProviderWithOriginQuery }
